import { zodResolver } from '@hookform/resolvers/zod';
import { addDays, addMonths, format } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useShallow } from 'zustand/react/shallow';
import { PaymentMethodEnum } from '@clickersphere/core/enums/PaymentMethodEnum';
import { SaleItemKindEnum } from '@clickersphere/core/enums/SaleItemKindEnum';
import { ISelectedItem } from '../../../../../app/hooks/useSelectableItems';
import { useRegisterLeadSale } from '../../../../../app/hooks/lead-sale/useRegisterLeadSale';
import { useSelectableItems } from '../../../../../app/hooks/useSelectableItems';
import { useServicePackages } from '../../../../../app/hooks/service-package/useServicePackages';
import { useServices } from '../../../../../app/hooks/service/useServices';
import { useStore } from '../../../../../app/store';
import { isPortugueseLanguage } from '../../../../../app/utils/resolveLocale';
import {
  EN_US_DATE_FORMAT,
  parseDate,
  PT_BR_DATE_FORMAT,
} from '@clickersphere/core/utils/parseDate';

type FormData = {
  totalPrice: number;
  paymentMethod: PaymentMethodEnum;
  installmentsCount: number;
  firstDueDate: string;
  soldAt: string;
  notes?: string;
};

function todayFormatted(dateFormat: string): string {
  return format(new Date(), dateFormat);
}

function plus30DaysFormatted(dateFormat: string): string {
  return format(addDays(new Date(), 30), dateFormat);
}

export function useRegisterSaleModalController(leadId: string) {
  const { t, i18n } = useTranslation();
  const dateFormat = isPortugueseLanguage(i18n.language)
    ? PT_BR_DATE_FORMAT
    : EN_US_DATE_FORMAT;
  const { handleCloseModal, saleDraft } = useStore(
    useShallow((state) => ({
      handleCloseModal: state.modal.handleCloseModal,
      saleDraft: state.modal.saleDraft,
    })),
  );

  const { registerSaleAsync, registerSaleIsPending } =
    useRegisterLeadSale(leadId);
  const { services } = useServices();
  const { servicePackages } = useServicePackages();

  const initialServiceItems = useMemo<ISelectedItem[]>(
    () =>
      (saleDraft?.items ?? [])
        .filter(
          (item) => item.kind === SaleItemKindEnum.SERVICE && !!item.originalId,
        )
        .map((item) => ({
          id: item.originalId as string,
          quantity: item.quantity,
        })),
    [saleDraft],
  );

  const initialPackageItems = useMemo<ISelectedItem[]>(
    () =>
      (saleDraft?.items ?? [])
        .filter(
          (item) => item.kind === SaleItemKindEnum.PACKAGE && !!item.originalId,
        )
        .map((item) => ({
          id: item.originalId as string,
          quantity: item.quantity,
        })),
    [saleDraft],
  );

  const {
    selectedItems: selectedServices,
    handleSelectionChange: handleServiceSelectionChange,
    handleQuantityChange: handleServiceQuantityChange,
    handleRemove: handleRemoveService,
    getSubtotal: getServiceSubtotal,
  } = useSelectableItems(services, i18n.language, initialServiceItems);

  const {
    selectedItems: selectedPackages,
    handleSelectionChange: handlePackageSelectionChange,
    handleQuantityChange: handlePackageQuantityChange,
    handleRemove: handleRemovePackage,
    getSubtotal: getPackageSubtotal,
  } = useSelectableItems(servicePackages, i18n.language, initialPackageItems);

  const [itemsError, setItemsError] = useState<string | undefined>(undefined);
  const [customizeInstallments, setCustomizeInstallments] = useState(false);
  const [installmentRows, setInstallmentRows] = useState<
    { amount: string; dueDate: string }[]
  >([]);
  const [installmentsError, setInstallmentsError] = useState<
    string | undefined
  >(undefined);

  const totalManuallyEdited = useRef(!!saleDraft);

  const schema = useMemo(
    () =>
      z
        .object({
          totalPrice: z
            .number({
              message: t('sale.registerModal.errors.totalInvalidType'),
            })
            .positive(t('sale.registerModal.errors.totalPositive')),
          paymentMethod: z.nativeEnum(PaymentMethodEnum, {
            message: t('sale.registerModal.errors.paymentMethodRequired'),
          }),
          installmentsCount: z
            .number({
              message: t('sale.registerModal.errors.installmentsInvalidType'),
            })
            .int()
            .min(1, t('sale.registerModal.errors.installmentsMin'))
            .max(24, t('sale.registerModal.errors.installmentsMax')),
          firstDueDate: z.string(),
          soldAt: z
            .string()
            .min(1, t('sale.registerModal.errors.soldAtRequired')),
          notes: z.string().optional(),
        })
        .superRefine((data, ctx) => {
          if (data.installmentsCount > 1) {
            if (!data.firstDueDate || data.firstDueDate.length < 10) {
              ctx.addIssue({
                path: ['firstDueDate'],
                code: z.ZodIssueCode.custom,
                message: t('sale.registerModal.errors.firstDueDateRequired'),
              });
            } else if (!parseDate(data.firstDueDate, dateFormat)) {
              ctx.addIssue({
                path: ['firstDueDate'],
                code: z.ZodIssueCode.custom,
                message: t('sale.registerModal.errors.firstDueDateInvalid'),
              });
            }
          }
          if (!parseDate(data.soldAt, dateFormat)) {
            ctx.addIssue({
              path: ['soldAt'],
              code: z.ZodIssueCode.custom,
              message: t('sale.registerModal.errors.soldAtInvalid'),
            });
          }
        }),
    [t, dateFormat],
  );

  const {
    handleSubmit: hookFormHandleSubmit,
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      totalPrice: saleDraft ? saleDraft.totalPrice / 100 : 0,
      paymentMethod: saleDraft?.paymentMethod ?? undefined,
      installmentsCount: saleDraft?.installmentsCount ?? 1,
      firstDueDate: plus30DaysFormatted(dateFormat),
      soldAt: todayFormatted(dateFormat),
      notes: saleDraft?.notes ?? '',
    },
  });

  const watchedInstallmentsCount = watch('installmentsCount');
  const watchedTotalPrice = watch('totalPrice');
  const watchedFirstDueDate = watch('firstDueDate');

  const buildEqualInstallments = useCallback(
    (count: number, total: number, firstDue: string) => {
      const safeCount = Math.max(1, Math.floor(count) || 1);
      const totalCents = Math.round((total || 0) * 100);
      const baseCents = Math.floor(totalCents / safeCount);
      const remainder = totalCents - baseCents * safeCount;
      const firstParsed = parseDate(firstDue, dateFormat) ?? new Date();

      return Array.from({ length: safeCount }, (_, index) => {
        const cents = baseCents + (index === safeCount - 1 ? remainder : 0);
        return {
          amount: (cents / 100).toFixed(2),
          dueDate: format(addMonths(firstParsed, index), dateFormat),
        };
      });
    },
    [dateFormat],
  );

  function handleToggleCustomizeInstallments(next: boolean) {
    setCustomizeInstallments(next);
    setInstallmentsError(undefined);
    if (next) {
      setInstallmentRows(
        buildEqualInstallments(
          watchedInstallmentsCount,
          watchedTotalPrice,
          watchedFirstDueDate,
        ),
      );
    }
  }

  function handleInstallmentRowChange(
    index: number,
    field: 'amount' | 'dueDate',
    value: string,
  ) {
    setInstallmentsError(undefined);
    setInstallmentRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row,
      ),
    );
  }

  useEffect(() => {
    if (!customizeInstallments) return;
    if (watchedInstallmentsCount <= 1) {
      setCustomizeInstallments(false);
      setInstallmentRows([]);
      return;
    }
    setInstallmentRows((prev) => {
      if (prev.length === watchedInstallmentsCount) return prev;
      return buildEqualInstallments(
        watchedInstallmentsCount,
        watchedTotalPrice,
        watchedFirstDueDate,
      );
    });
  }, [
    customizeInstallments,
    watchedInstallmentsCount,
    watchedTotalPrice,
    watchedFirstDueDate,
    buildEqualInstallments,
  ]);

  const installmentsSum = useMemo(
    () =>
      installmentRows.reduce(
        (acc, row) => acc + (parseFloat(row.amount) || 0),
        0,
      ),
    [installmentRows],
  );

  useEffect(() => {
    if (totalManuallyEdited.current) return;

    const serviceTotal = selectedServices.reduce((acc, sel) => {
      const svc = services?.find((s) => s.id === sel.id);
      return acc + (svc ? (svc.price / 100) * sel.quantity : 0);
    }, 0);

    const packageTotal = selectedPackages.reduce((acc, sel) => {
      const pkg = servicePackages?.find((p) => p.id === sel.id);
      return acc + (pkg ? (pkg.price / 100) * sel.quantity : 0);
    }, 0);

    setValue(
      'totalPrice',
      parseFloat((serviceTotal + packageTotal).toFixed(2)),
    );
  }, [selectedServices, selectedPackages, services, servicePackages, setValue]);

  const handleTotalPriceChange = useCallback(() => {
    totalManuallyEdited.current = true;
  }, []);

  const serviceOptions = (services ?? [])
    .filter((s) => !s.deletedAt)
    .map((s) => ({ value: s.id, label: s.name }));

  const packageOptions = (servicePackages ?? [])
    .filter((p) => !p.deletedAt)
    .map((p) => ({ value: p.id, label: p.name }));

  const paymentMethodOptions = useMemo(
    () => [
      { value: PaymentMethodEnum.PIX, text: t('sale.paymentMethod.PIX') },
      { value: PaymentMethodEnum.CASH, text: t('sale.paymentMethod.CASH') },
      {
        value: PaymentMethodEnum.CREDIT_CARD,
        text: t('sale.paymentMethod.CREDIT_CARD'),
      },
      {
        value: PaymentMethodEnum.DEBIT_CARD,
        text: t('sale.paymentMethod.DEBIT_CARD'),
      },
      {
        value: PaymentMethodEnum.BANK_TRANSFER,
        text: t('sale.paymentMethod.BANK_TRANSFER'),
      },
      { value: PaymentMethodEnum.BOLETO, text: t('sale.paymentMethod.BOLETO') },
      { value: PaymentMethodEnum.OTHER, text: t('sale.paymentMethod.OTHER') },
    ],
    [t],
  );

  const handleSubmit = hookFormHandleSubmit(async (data) => {
    const allItems = [...selectedServices, ...selectedPackages];
    if (allItems.length === 0) {
      setItemsError(t('sale.registerModal.errors.itemsMin'));
      return;
    }
    setItemsError(undefined);

    const soldAtParsed = parseDate(data.soldAt, dateFormat);
    const firstDueDateParsed =
      data.installmentsCount > 1
        ? parseDate(data.firstDueDate, dateFormat)
        : soldAtParsed;

    const items = [
      ...selectedServices.map((s) => ({
        kind: SaleItemKindEnum.SERVICE,
        id: s.id,
        quantity: s.quantity,
      })),
      ...selectedPackages.map((p) => ({
        kind: SaleItemKindEnum.PACKAGE,
        id: p.id,
        quantity: p.quantity,
      })),
    ];

    const totalCents = Math.round(data.totalPrice * 100);
    const useCustomInstallments =
      customizeInstallments && data.installmentsCount > 1;

    let installments;
    if (useCustomInstallments) {
      const parsedRows = installmentRows.map((row) => ({
        amountInCents: Math.round((parseFloat(row.amount) || 0) * 100),
        parsedDate: parseDate(row.dueDate, dateFormat),
      }));

      if (parsedRows.some((row) => !row.parsedDate)) {
        setInstallmentsError(
          t('sale.registerModal.errors.installmentsDateInvalid'),
        );
        return;
      }

      const sumCents = parsedRows.reduce(
        (acc, row) => acc + row.amountInCents,
        0,
      );
      if (sumCents !== totalCents) {
        setInstallmentsError(
          t('sale.registerModal.errors.installmentsSumMismatch'),
        );
        return;
      }

      installments = parsedRows.map((row) => ({
        amountInCents: row.amountInCents,
        dueDate: row.parsedDate!.toISOString(),
      }));
    }

    setInstallmentsError(undefined);

    await registerSaleAsync({
      soldAt: soldAtParsed!.toISOString(),
      totalPrice: totalCents,
      installmentsCount: data.installmentsCount,
      paymentMethod: data.paymentMethod,
      notes: data.notes || undefined,
      items,
      firstDueDate: firstDueDateParsed?.toISOString(),
      installments,
    });

    handleCloseModal();
  });

  return {
    register,
    control,
    errors,
    itemsError,
    watchedInstallmentsCount,
    watchedTotalPrice,
    customizeInstallments,
    installmentRows,
    installmentsSum,
    installmentsError,
    handleToggleCustomizeInstallments,
    handleInstallmentRowChange,
    registerSaleIsPending,
    serviceOptions,
    packageOptions,
    paymentMethodOptions,
    selectedServices,
    selectedPackages,
    services: services ?? [],
    servicePackages: servicePackages ?? [],
    handleServiceSelectionChange,
    handlePackageSelectionChange,
    handleServiceQuantityChange,
    handlePackageQuantityChange,
    handleRemoveService,
    handleRemovePackage,
    handleTotalPriceChange,
    getServiceSubtotal,
    getPackageSubtotal,
    handleSubmit,
    handleClose: handleCloseModal,
  };
}
